// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { TimelineColumns } from '@/components/timeline-columns';
import { useTheme } from '@mui/material';
import { AxisScale, AxisTop } from '@visx/axis';
import { NumberLike } from '@visx/scale';
import { Line } from '@visx/shape';
import { DateTime, Interval } from 'luxon';

export type TimelineAxisDensity = 'day' | 'week' | 'month';

/**
 * Formats a date for display on the date axis.
 *
 * @param date - The date to format.
 */
function formatTick(date: Date | NumberLike) {
  const dateTime = DateTime.fromMillis(date.valueOf());

  return dateTime.toLocaleString({ month: 'short', day: 'numeric' });
}

interface Props {
  scale: AxisScale<number>;
  width: number;
  axisHeight: number;
  chartHeight: number;
  density: TimelineAxisDensity;
  interval: Interval;
}

export function TimelineAxis(props: Props) {
  const theme = useTheme();

  const scaleIntervals = props.interval.splitBy({ [props.density]: 1 });
  const scaleTicks = scaleIntervals.map((interval) => interval.start);

  return (
    <>
      <AxisTop
        hideTicks
        hideAxisLine
        scale={props.scale}
        top={props.axisHeight}
        stroke={theme.palette.grey[300]}
        tickValues={scaleTicks}
        tickFormat={formatTick}
        tickLabelProps={{
          fontFamily: theme.typography.fontFamily,
          color: theme.palette.text.primary,
          fontSize: theme.typography.caption.fontSize,
        }}
      />
      <Line
        stroke={theme.palette.divider}
        strokeWidth={1}
        to={{ x: props.width, y: props.axisHeight }}
        from={{ x: 0, y: props.axisHeight }}
      />
      <TimelineColumns
        scale={props.scale}
        y={props.axisHeight}
        height={props.chartHeight}
        density={props.density}
        interval={props.interval}
      />
    </>
  );
}
